import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import api, { subscribeEvents } from '../services/api';
import { formatDate, formatConsumption } from '../utils/format';

const CriticalsPage = () => {
  const [criticalWaterMeters, setCriticalWaterMeters] = useState([]);
  const [alertsByMeter, setAlertsByMeter] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery('(max-width:800px)');

  const PAGE_SIZE = 20;

  // AbortController refs
  const totalCountAbortControllerRef = useRef();
  const criticalsAbortControllerRef = useRef();
  const loadMoreAbortControllerRef = useRef();

  // Fetch total count of critical water meters
  const fetchTotalCount = async () => {
    const controller = new AbortController();
    totalCountAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const res = await api.get('/api/proxy/wmeters/critical/count', { signal });
      setTotalCount(res.data.count);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
  };

  // Fetch initial or refreshed list
  const fetchCriticals = async date => {
    setLoading(true);
    const controller = new AbortController();
    criticalsAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const params = { max: PAGE_SIZE };
      if (date) params.start = date.toISOString();
      const res = await api.get('/api/proxy/wmeters/critical', { params, signal });
      const meters = res.data;
      meters.sort((a, b) => new Date(b.lastseen) - new Date(a.lastseen));
      setCriticalWaterMeters(meters);
      // fetch alerts for initial batch
      const entries = await Promise.all(
        meters.map(meter => {
          const id = meter._id.$oid || meter._id;
          return api
            .get(
              `/api/proxy/wmeters/critical/${id}`,
              { params: { onlyRaised: 'true' }, signal }
            )
            .then(r => [id, r.data])
            .catch(() => [id, []]);
        })
      );
      setAlertsByMeter(Object.fromEntries(entries));
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
    setLoading(false);
  };

  // Load more older items without resetting list
  const loadMore = async () => {
    if (!criticalWaterMeters.length) return;
    setLoadingMore(true);
    const controller = new AbortController();
    loadMoreAbortControllerRef.current = controller;
    const signal = controller.signal;

    try {
      const oldestMs = Math.min(
        ...criticalWaterMeters.map(m => new Date(m.registeredAt).getTime())
      );
      const params = {
        max: PAGE_SIZE,
        start: new Date(oldestMs).toISOString(),
        reverse: true
      };
      const res = await api.get('/api/proxy/wmeters/critical', { params, signal });
      let newBatch = res.data.filter(m => {
        const id = m._id.$oid || m._id;
        return !criticalWaterMeters.some(existing => (existing._id.$oid || existing._id) === id);
      });
      newBatch.sort((a, b) => new Date(b.lastseen) - new Date(a.lastseen));
      setCriticalWaterMeters(prev => [...prev, ...newBatch]);
      // fetch alerts for new batch
      const newEntries = await Promise.all(
        newBatch.map(meter => {
          const id = meter._id.$oid || meter._id;
          return api
            .get(
              `/api/proxy/wmeters/critical/${id}`,
              { params: { onlyRaised: 'true' }, signal }
            )
            .then(r => [id, r.data])
            .catch(() => [id, []]);
        })
      );
      setAlertsByMeter(prev => ({ ...prev, ...Object.fromEntries(newEntries) }));
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
      }
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchTotalCount();
    fetchCriticals(startDate);

    return () => {
      totalCountAbortControllerRef.current?.abort();
      criticalsAbortControllerRef.current?.abort();
      loadMoreAbortControllerRef.current?.abort();
    };
  }, [startDate]);

  /* Live update: re-fetch alert list for the affected meter when an alert
   * notification arrives via SSE (raised or fixed). */
  const refreshAlertsForMeter = useCallback(async (meterId) => {
    if (!meterId) return;
    try {
      const r = await api.get(`/api/proxy/wmeters/critical/${meterId}`, {
        params: { onlyRaised: 'true' }
      });
      setAlertsByMeter(prev => ({ ...prev, [meterId]: r.data }));
      /* Also refresh total count and meter list so newly-cleared meters
       * disappear and newly-critical ones appear. */
      fetchTotalCount();
      fetchCriticals(startDate);
    } catch (_) {}
  }, [startDate]);

  useEffect(() => {
    const sse = subscribeEvents('/api/notifications', {
      onmessage: event => {
        try {
          const data = JSON.parse(event.data);
          /* alert events carry meterId, enabling a targeted refresh */
          if (data.meterId) {
            refreshAlertsForMeter(data.meterId);
          }
        } catch (_) {}
      },
      onerror: () => {}
    });
    return () => sse?.abort();
  }, [refreshAlertsForMeter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={isSmall ? 1 : 2}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isSmall ? 1 : 2 }}
      >
        <Typography variant="h5">Critical Water Meters ({totalCount})</Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start date"
            value={startDate}
            onChange={newDate => setStartDate(newDate)}
            renderInput={params => <TextField {...params} size="small" sx={{ minWidth: 140 }} />}
          />
        </LocalizationProvider>
      </Box>

      {criticalWaterMeters.map(device => {
        const id = device._id.$oid || device._id;
        const statusColor = 'red';
        return (
          <Paper key={id} sx={{ mb:  isSmall ? 1 : 2, p: isSmall ? 1 : 2, cursor: 'pointer' }} onClick={() => navigate(`/water-meters/${id}`)}>
            <Grid container direction={isSmall ? 'column' : 'row'} alignItems="center" spacing={1}>
              <Grid item>
                <FiberManualRecordIcon sx={{ color: statusColor }} fontSize="small" />
              </Grid>
              <Grid item xs>
                <Typography variant={isSmall ? 'body1' : 'subtitle1'}>{device.deviceName}</Typography>
                <Typography variant="caption" color="textSecondary">Water Meter</Typography>
              </Grid>
              {!isSmall && (
                <Grid item>
                  <Typography variant="body2">BLE: {device.bleAddress}</Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: isSmall ? 1 : 2 }} />

            <Grid container direction={isSmall ? 'column' : 'row'} spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">Last Seen: {device.lastseen ? formatDate(device.lastseen, true) : 'N/A'}</Typography>
                {(alertsByMeter[id] || []).map((alert, idx) => (
                  <Typography key={idx} variant="body2" sx={{ mt: 0.5, pl: 1 }}>• {alert.type} — {alert.status}</Typography>
                ))}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">Consumption: {formatConsumption(device.totalConsumption)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">Device Status: {device.deviceStatus}</Typography>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      {criticalWaterMeters.length > 0 && (
        <>
          <Box textAlign="center" mt={2}>
            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load More'}
            </Button>
          </Box>
          <Box textAlign="right" mt={1}>{criticalWaterMeters.length} of {totalCount}</Box>
        </>
      )}
    </Box>
  );
};

export default CriticalsPage;
